import React, { useState, useEffect } from 'react';
import {
  useQueryParams,
  StringParam,
  withDefault,
  NumberParam,
} from 'use-query-params';
import { useTranslation } from 'react-i18next';

import { useAlert } from 'react-alert';
import { useLoading } from '../../../commons/Loading';

import contractsAPIs from '../../../lib/fetch/contractsAPIs';
import EnergyPresentational from './EnergyPresentational';
import { useAuth } from '../../../commons/Auth';
import axios from 'axios';
import moment from 'moment';
import { getSignedUrl } from '../../../lib/fetch/files';
const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const Contracts = ({ profile }) => {
  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();

  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totPod, setTotPod] = useState(0);
  const [totPdr, setTotPdr] = useState(0);

  const [selectedContracts, setSelectedContracts] = useState([]);
  const [checkedRows, setCheckedRows] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [hasImportedIafPolis, setHasImportedIafPolis] = useState(false);
  const [iafPolisImportFeedbackData, setIafPolisImportFeedbackData] = useState(
    {}
  );

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    pod_pdr: withDefault(StringParam, ''),
    owner: withDefault(StringParam, ''),
    holder: withDefault(StringParam, ''),
    condo_vat_cf: withDefault(StringParam, ''),
    address: withDefault(StringParam, ''),
    customer: withDefault(StringParam, ''),
    customerType: withDefault(StringParam, ''),
    managementStatus: withDefault(StringParam, ''),
    contractActivationYear: NumberParam,
    contractActivationMonth: NumberParam,
    contractType: withDefault(StringParam, ''),
    iafPolis: withDefault(StringParam, ''),
  });

  const {
    limit,
    offset,
    admin,
    condo_vat_cf,
    owner,
    holder,
    pod_pdr,
    customer,
    customerType,
    managementStatus,
    contractActivationYear,
    contractActivationMonth,
    contractType,
    iafPolis,
  } = query;

  const toggleShowImportIafPolisFeedback = () =>
    setHasImportedIafPolis((value) => !value);

  function onFilter(queryDiff) {
    if (
      (queryDiff.admin && queryDiff.admin !== admin) || // first search
      (admin && queryDiff.admin === '') || // search deleted
      (queryDiff.pod_pdr && queryDiff.pod_pdr !== pod_pdr) || // first search
      (pod_pdr && queryDiff.pod_pdr === '') || // search deleted
      (queryDiff.contractActivationYear &&
        queryDiff.contractActivationYear !== contractActivationYear) || // first search
      (contractActivationYear && queryDiff.contractActivationYear === '') || // search deleted
      (queryDiff.contractActivationMonth &&
        queryDiff.contractActivationMonth !== contractActivationMonth) || // first search
      (contractActivationMonth && queryDiff.contractActivationMonth === '') || // search deleted
      (queryDiff.customer && queryDiff.customer !== customer) || // first search
      (customer && queryDiff.customer === '') || // search deleted
      (queryDiff.customerType && queryDiff.customerType !== customerType) || // first search
      (customerType && queryDiff.customerType === '') || // search deleted
      (queryDiff.contractType && queryDiff.contractType !== contractType) || // first search
      (contractType && queryDiff.contractType === '') || // search deleted
      (queryDiff.managementStatus &&
        queryDiff.managementStatus !== managementStatus) || // first search
      (managementStatus && queryDiff.managementStatus === '') || // search deleted
      (queryDiff.condo_vat_cf && queryDiff.condo_vat_cf !== condo_vat_cf) || // first search
      (condo_vat_cf && queryDiff.condo_vat_cf === '') || // search deleted
      (queryDiff.owner && queryDiff.owner !== owner) || // first search
      (owner && queryDiff.owner === '') || // search deleted
      (queryDiff.holder && queryDiff.holder !== holder) || // first search
      (holder && queryDiff.holder === '') || // search deleted
      (queryDiff.iafPolis && queryDiff.iafPolis !== iafPolis) || // first search
      (iafPolis && queryDiff.iafPolis === '') ||
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const fetchContracts = async (query) => {
    showLoading();

    const {
      data,
      total: _total,
      totPod,
      totPdr,
    } = await contractsAPIs.findUserContracts({
      newToken,
      query,
      userId: profile._id,
    });
    setTotal(Number(_total || 0));
    setTotPod(totPod);
    setTotPdr(totPdr);
    setContracts(data);

    hideLoading();
  };

  useEffect(() => {
    if (profile._id) {
      fetchContracts({
        pod_pdr,
        condo_vat_cf,
        owner,
        holder,
        offset,
        limit,
        customer,
        customerType,
        contractType,
        managementStatus,
        contractActivationYear,
        contractActivationMonth,
        iafPolis,
      });
    }
  }, [
    condo_vat_cf,
    pod_pdr,
    owner,
    holder,
    offset,
    limit,
    customer,
    customerType,
    contractType,
    managementStatus,
    contractActivationYear,
    contractActivationMonth,
    iafPolis,
  ]);

  const onImportIafPolis = async (file) => {
    showLoading();
    const { error, data } = await contractsAPIs.importIafPolis({
      newToken,
      file,
      profileId: profile._id,
    });
    hideLoading();
    toggleShowImportIafPolisFeedback();
    setIafPolisImportFeedbackData({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchContracts({
      pod_pdr,
      condo_vat_cf,
      owner,
      holder,
      offset,
      limit,
      customer,
      customerType,
      contractType,
      managementStatus,
      contractActivationYear,
      contractActivationMonth,
      iafPolis,
    });
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    }
  };

  const onSelectChange = (row) => {
    if (selectedContracts.includes(row._id)) {
      setSelectedAll(false);
      setCheckedRows((checkedRows) =>
        checkedRows.filter(({ _id }) => _id !== row._id)
      );
      return setSelectedContracts((selectedContracts) =>
        selectedContracts.filter((contractId) => contractId !== row._id)
      );
    }
    setSelectedContracts((selectedContracts) => [
      ...selectedContracts,
      row._id,
    ]);
    setCheckedRows((checkedRows) => [...checkedRows, row]);
  };

  const onSelectAll = (selectAll) => {
    setSelectedAll(selectAll);
    if (!selectAll) {
      setCheckedRows([]);
      return setSelectedContracts([]);
    }
    const billableContracts = contracts.filter(
      (contract) =>
        !(contract.pinvInvoice?.length > 0) &&
        (contract.partnerEonType === 'STORNO_GETTONE' ||
          contract.contract_value > 0)
    );
    setCheckedRows(billableContracts);
    setSelectedContracts(billableContracts.map(({ _id }) => _id));
  };

  const onInvoice = async () => {
    showLoading();

    const signingDateYear = moment(
      checkedRows[0]?.activation_start_date
    ).year();

    for (const row of checkedRows) {
      const rowYear = moment(row.activation_start_date).year();
      if (rowYear !== signingDateYear) {
        alert.error(
          'I contratti selezionati presentano anni di attivazione differenti.'
        );
        hideLoading();
        return;
      }
    }

    const totalToInvoice = Number(
      checkedRows
        .reduce(
          (total, row) =>
            total + Number(Number(row.contract_value || 0).toFixed(2)),
          0
        )
        .toFixed(2)
    );

    if (totalToInvoice <= 0) {
      alert.error(t('COMMON:ERROR_TOTAL_TO_INVOICE_0'));
      hideLoading();
      return;
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/extra-invoices/${profile._id}/contracts`,
      {
        selectedContracts,
      },
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));

    fetchContracts({
      pod_pdr,
      condo_vat_cf,
      owner,
      holder,
      offset,
      limit,
      customer,
      customerType,
      contractType,
      managementStatus,
      contractActivationYear,
      contractActivationMonth,
    });
    setSelectedContracts([]);
    setSelectedAll(false);
  };

  async function handleFileListView(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  return (
    <EnergyPresentational
      data={contracts}
      profile={profile}
      query={query}
      total={total}
      totPod={totPod}
      totPdr={totPdr}
      onFilter={onFilter}
      selectedContracts={selectedContracts}
      onInvoice={onInvoice}
      selectedAll={selectedAll}
      onSelectAll={onSelectAll}
      onSelectChange={onSelectChange}
      onStatusChanged={() => fetchContracts()}
      onImportIafPolis={onImportIafPolis}
      toggleShowImportIafPolisFeedback={toggleShowImportIafPolisFeedback}
      iafPolisImportFeedbackData={iafPolisImportFeedbackData}
      hasImportedIafPolis={hasImportedIafPolis}
      handleFileListView={handleFileListView}
    />
  );
};

export default Contracts;
